import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/af04cee4-1d95-42e7-9f81-e2167e421d48/images/2c42297793b46ffab5bc5a1401952ac36cdbcbe33b95cd75886e67eaa8f10005.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-[#0066FF] to-[#00FF99] rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-black font-black text-2xl">BV</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">{t('auth.login.title')}</h1>
          <p className="text-zinc-400">{t('auth.login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-6">
          {error && (
            <div data-testid="error-message" className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.login.email_label')}</label>
            <input
              type="email"
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.login.email_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.login.password_label')}</label>
            <input
              type="password"
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.login.password_placeholder')}
            />
          </div>

          <button
            type="submit"
            data-testid="submit-button"
            disabled={loading}
            className="w-full bg-[#0066FF] text-white hover:bg-[#3385FF] transition-all duration-300 font-bold tracking-wide rounded-lg px-6 py-3 shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_30px_rgba(0,102,255,0.6)] disabled:opacity-50"
          >
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>

        <p className="text-center text-zinc-400 mt-6">
          {t('auth.login.no_account')}{' '}
          <a href="/register" className="text-[#00FF99] hover:text-[#33FFB0] font-semibold transition-colors">
            {t('auth.login.signup_link')}
          </a>
        </p>
      </motion.div>
    </div>
  );
}

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(name, email, password, country);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/af04cee4-1d95-42e7-9f81-e2167e421d48/images/2c42297793b46ffab5bc5a1401952ac36cdbcbe33b95cd75886e67eaa8f10005.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-[#0066FF] to-[#00FF99] rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-black font-black text-2xl">BV</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">{t('auth.register.title')}</h1>
          <p className="text-zinc-400">{t('auth.register.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-5">
          {error && (
            <div data-testid="error-message" className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.register.name_label')}</label>
            <input
              type="text"
              data-testid="name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.register.name_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.register.email_label')}</label>
            <input
              type="email"
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.register.email_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.register.country_label')}</label>
            <input
              type="text"
              data-testid="country-input"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.register.country_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{t('auth.register.password_label')}</label>
            <input
              type="password"
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              placeholder={t('auth.register.password_placeholder')}
            />
          </div>

          <button
            type="submit"
            data-testid="submit-button"
            disabled={loading}
            className="w-full bg-[#0066FF] text-white hover:bg-[#3385FF] transition-all duration-300 font-bold tracking-wide rounded-lg px-6 py-3 shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_30px_rgba(0,102,255,0.6)] disabled:opacity-50"
          >
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>

        <p className="text-center text-zinc-400 mt-6">
          {t('auth.register.have_account')}{' '}
          <a href="/login" className="text-[#00FF99] hover:text-[#33FFB0] font-semibold transition-colors">
            {t('auth.register.login_link')}
          </a>
        </p>
      </motion.div>
    </div>
  );
}
